<!-- Component docs: https://vitroweb.sharepoint.com/:u:/r/Company/Informatica/I+D/DDSW%20Productos/AnaPath/Desarrollo/Documentaci%C3%B3n/Frontend/Componentes/VtComboSelect.html?csf=1&web=1&e=sYCMcl -->
<template>
  <v-multiselect
    :id="id"
    v-bind="$attrs"
    ref="multiselect"
    :class="{ 'selected-value': highlightWhenSelectedValue && value }"
    :value="internalValue"
    :searchable="searchable"
    :label="label"
    :custom-label="customLabel"
    :multiple="multiple"
    :track-by="internalTrackBy"
    :options="internalOptions"
    :show-labels="false"
    :max-height="maxHeight"
    :placeholder="placeholder"
    :loading="internalLoading"
    :disabled="disabled"
    :open-direction="openDirection"
    :internal-search="internalSearch"
    :taggable="taggable"
    :tag-placeholder="tagPlaceholder"
    :data-vv-scope="dataVvScope"
    :data-vv-name="name"
    :allow-empty="allowEmpty"
    @input="onInput"
    @search-change="onSearchChange"
    @remove="$emit('remove', $event)"
    @select="onSelect"
    @keydown.enter.native="onKeyUp"
    @keydown.tab.native="onTab"
    @tag="onTag"
    @close="$emit('close')"
    @open="onOpen"
  >
    <template #noOptions>
      <slot name="noOptions">
        {{ $tc('component.vueMultiselect.noOptions') }}
      </slot>
    </template>
    <template #noResult="{ search }">
      <slot
        name="noResult"
        :search="search"
      >
        {{ $tc('component.vueMultiselect.noResult') }}
      </slot>
    </template>
    <template #singleLabel="{ option }">
      <slot
        name="singleLabel"
        :option="option"
      >
        <vt-combo-select-label-renderer
          :option="option"
          :label="label"
          :custom-label="customLabel"
          :icon="icon"
        />
      </slot>
    </template>
    <template #option="{ option, search, index }">
      <slot
        name="option"
        :option="option"
        :search="search"
        :index="index"
      >
        <vt-combo-select-label-renderer
          :option="option"
          :label="label"
          :custom-label="customLabel"
          :icon="icon"
        />
      </slot>
    </template>
    <template
      v-if="showSelectionNumber"
      #selection="{ search, remove, values, isOpen }"
    >
      <slot
        name="selection"
        :search="search"
        :remove="remove"
        :values="values"
        :is-open="isOpen"
      >
        <span
          v-if="values.length && !isOpen"
          class="multiselect__single"
        >
          {{ values.length }} {{ $tc('global.selectedOption', values.length) }}
        </span>
      </slot>
    </template>
  </v-multiselect>
</template>

<script>
import VtComboSelectLabelRenderer
    from '@/components/common/vt-combo-select/components/vt-combo-select-label-renderer/vt-combo-select-label-renderer.vue'
import { cloneDeep, debounce, isEmpty, uniqueId } from 'lodash'
import { TextNormalization } from '@/shared/util/text-normalization'

export default {
    name: 'VtComboSelect',
    components: {
        VtComboSelectLabelRenderer
    },
    props: {
        internalSearch: { type: Boolean, default: false },
        showSelectionNumber: { type: Boolean, default: false },
        searchable: { type: Boolean, default: true },
        focus: { type: Boolean, required: false, default: false },
        maxHeight: { type: Number, default: () => 150 },
        id: { type: String },
        value: { type: [Object, Array, String], required: false },
        openDirection: { type: String, default: '' },
        placeholder: {
            type: String,
            default: function() {
                return this.$tc('component.vueMultiselect.placeholder')
            }
        },
        label: { type: String },
        customLabel: { type: Function },
        icon: { type: String },
        // Use one or the other, not both. optionLoader requires less code in the parent component.
        // But If you want to control the options array from the parent component, use options.
        options: { type: Array, default: () => [] },
        optionLoader: { type: Function }, // returns a promise that resolves to an array of options
        loading: { type: Boolean, default: false },
        minCharsToFilter: { type: Number, default: 2 },
        multiple: { type: Boolean },
        trackBy: { type: String }, // necessary to avoid weird behaviour on lists with multiple selection, used to compare objects.
        ajaxSearch: { type: Boolean, default: false }, // loads options from API on search change. Internal search can be disabled separately.
        searchOnEmptyText: { type: Boolean, default: true },
        disabled: { type: Boolean, default: false },
        dataVvScope: { type: String },
        taggable: { type: Boolean, default: false },
        tagPlaceholder: { type: String },
        name: { type: String, default: () => uniqueId('comboSelect') },
        highlightWhenSelectedValue: { type: Boolean, default: false },
        allowEmpty: { type: Boolean, default: true },
        optionsToBeDisabled: { type: Array, default: () => [] },
        debounceMillis: { type: Number, default: 200 }
    },
    data() {
        return {
            internalLoading: false,
            internalValue: this.value,
            internalOptions: [],
            internalOriginalOptions: [],
            onSearchChange: () => {}
        }
    },
    computed: {
        internalTrackBy() {
            const isObjectArray = Array.isArray(this.internalOptions) && !this.internalOptions.some((value) => typeof value !== 'object')
            return isObjectArray ? (this.trackBy ?? 'id') : ''
        }
    },
    watch: {
        value(value) {
            this.internalValue = cloneDeep(value)
        },
        options(options) {
            this.internalOriginalOptions = this.options
            this.handleAssignment(options)
        },
        loading(loading) {
            this.internalLoading = loading
        }
    },
    async created() {
        this.defineDebounced()
        this.internalOriginalOptions = this.options
        if (this.optionLoader && !this.options.length && (!this.ajaxSearch || this.searchOnEmptyText)) {
            this.internalOriginalOptions = await this.loadOptions()
        }
        this.handleAssignment(this.internalOriginalOptions)
    },
    mounted() {
        if (this.focus) {
            this.$refs.multiselect.activate()
        }
    },
    methods: {
        defineDebounced() {
            this.onSearchChange = debounce(async function(query) {
                if (this.ajaxSearch && this.optionLoader) {
                    if (query.length >= this.minCharsToFilter || (query === '' && this.searchOnEmptyText)) {
                        const options = await this.loadOptions(query)
                        this.handleAssignment(options)
                    } else if (query || !this.searchOnEmptyText) {
                        this.clearInternalOptions()
                    }
                } else if (query) {
                    const optionToString = this.customLabel ? this.customLabel : option => option[this.label]
                    this.internalOptions = this.internalOriginalOptions.filter(option => TextNormalization.normalizedContains(query, optionToString(option)))
                } else {
                    this.internalOptions = this.internalOriginalOptions
                }
            }.bind(this), this.debounceMillis)
        },
        onOpen() {
            if (this.optionsToBeDisabled.length) {
                this.internalOptions.forEach((option) => {
                    option.$isDisabled = this.isOptionDisabled(option)
                })
            }
            this.$emit('open')
        },
        onTag(newTag) {
            const tag = {
                [this.internalTrackBy]: newTag,
                [this.label]: newTag
            }
            this.$emit('tag', tag)
        },
        isOptionDisabled(option) {
            return !!this.optionsToBeDisabled.some(optionToBeDisabled => {
                if (optionToBeDisabled) {
                    return (optionToBeDisabled[this.internalTrackBy] === option[this.internalTrackBy])
                }
            })
        },

        setInternalOptions(options) {
            this.internalOptions = cloneDeep(options)
        },

        clearInternalOptions() {
            this.internalOptions = []
            this.internalLoading = false
        },

        handleOptions(options, value) {
            const isValueOnOptions = options.find(option => option[this.internalTrackBy] === value[this.internalTrackBy])
            if (!isValueOnOptions) {
                value.externalOption = true
                options.unshift(value)
            }
        },

        handleOptionsAssignmentForMultipleSelection(options) {
            if (!isEmpty(this.internalValue)) {
                this.internalValue.forEach(arrayValueItem => this.handleOptions(options, arrayValueItem))
            }
        },
        handleOptionsAssignment(options) {
            if (!isEmpty(this.internalValue)) {
                this.handleOptions(options, this.internalValue)
            }
        },
        handleAssignment(options) {
            if (this.multiple) {
                this.handleOptionsAssignmentForMultipleSelection(options)
            } else {
                this.handleOptionsAssignment(options)
            }

            this.setInternalOptions(options)
        },
        onTab() {
            let pointer = this.$refs.multiselect.pointer
            let filteredOptions = this.$refs.multiselect.filteredOptions
            this.onInput(filteredOptions[pointer])
        },
        onKeyUp() {
            let pointer = this.$refs.multiselect && this.$refs.multiselect.pointer
            let filteredOptions = this.$refs.multiselect && this.$refs.multiselect.filteredOptions
            if (!isNaN(pointer) && filteredOptions) {
                this.$emit('selectGrid', filteredOptions[pointer])
            }
        },
        onSelect(value) {
            this.$emit('select', value)
        },
        onInput(value) {
            this.$emit('input', value)
        },

        async loadOptions(query) {
            this.internalLoading = true
            try {
                return await this.optionLoader(query)
            } finally {
                this.internalLoading = false
            }
        },

        activateDropdown() {
            this.$refs.multiselect.activate()
        },
        deactivateDropdown() {
            this.$refs.multiselect.deactivate()
        }
    }
}
</script>
